// Storage shape for lookup-backed fields that persist as `string[]`.
//
// A growing set of reference fields are multi-select and store an ARRAY of
// slugs rather than a scalar:
//
//   dialAndHands.indexes   Marker Type   lookup_dial_marker_type
//   dialAndHands.numerals  Numerals      lookup_dial_numerals
//   crownType              Crown Type    lookup_crown_types
//   crystal                Crystal       lookup_crystal_materials
//
// The same helpers also carry CalibreData.balanceType, which went multi-select
// in shared-types v1.91.0 (see calibreBalanceTypeMigration.ts). That one is on
// the calibre document rather than the reference, but the transition problem is
// identical: scalars are already stored, and the migration is a separate step.
//
// They all share one problem, which is why they share these helpers. Every doc
// written before that field's migration holds a bare string — and scalars keep
// ARRIVING, from Brand Portal edits (watchlock's portal callable still
// validates crownType/crystal as `asString`), from bulk import, and from AI
// responses. So every READ goes through `parseMultiLookupValues` (accepts
// string | string[] | absent) and every WRITE through
// `multiLookupValuesForWrite`. The editor, the bulk tools and the portal have
// to agree on the shape or the migration just has to run again.
//
// Deliberately NOT the comma-joined convention used by handsColor / indexColor
// / handTypes: those three are joined strings because the RN app splits them on
// display. These are arrays because that is what the schema says; the RN read
// side is a tracked follow-up per field.

/**
 * Normalize a stored (or AI-suggested) multi-select value to `string[]`.
 *
 * Accepts the array shape, the legacy scalar, and the comma/semicolon-joined
 * string an AI response or a free-text brand-portal edit can produce. Anything
 * else (number, object, null) yields an empty array rather than a junk entry.
 *
 * Splitting on `,`/`;` is what turns a pre-migration scalar into something the
 * multi-picker can show — a raw import string like "Screw-down, Twinlock double
 * waterproofness system" becomes two custom chips instead of one unreadable
 * one. Lookup slugs never contain a separator, so nothing canonical is split.
 */
export function parseMultiLookupValues(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean);
  }
  if (typeof raw === 'string') {
    return raw
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * The value to persist: the array itself, or `undefined` when nothing is
 * selected so `removeUndefined` strips the key.
 *
 * Callers on an UPDATE path that must persist a CLEAR should write `[]`
 * explicitly instead — `undefined` is stripped before it reaches Firestore and
 * would leave the stale value in place (the same trap `dialAndHands.color`
 * documents by writing '' rather than undefined).
 */
export function multiLookupValuesForWrite(
  values: string[],
): string[] | undefined {
  const cleaned = values.map((v) => v.trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned : undefined;
}

/** Display string for such a field, whatever shape it is stored in. */
export function formatMultiLookupValues(raw: unknown): string {
  return parseMultiLookupValues(raw).join(', ');
}

/**
 * The value to persist on an UPDATE path, where an empty selection may need to
 * overwrite a stored one.
 *
 * `undefined` is stripped by `removeUndefined` before the patch reaches
 * Firestore, so clearing a field has to write something — `[]`. But writing
 * `[]` unconditionally stamps an empty array onto every ref that never had a
 * value, which is exactly what the migrations avoid: an empty array reads as
 * "curated as none" to consumers and keeps the ref in the "has a value" bucket
 * for the fill-empty bulk tools. So `[]` is written only when `stored` actually
 * held something.
 */
export function clearedArrayForWrite(
  values: string[],
  stored: unknown,
): string[] | undefined {
  const cleaned = values.map((v) => v.trim()).filter(Boolean);
  if (cleaned.length > 0) return cleaned;
  return parseMultiLookupValues(stored).length > 0 ? [] : undefined;
}
